import { faChevronDown, faSquare } from '@fortawesome/pro-regular-svg-icons';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import InputText from '../InputText';

import * as style from './style.module.css';

/**
 * Dropdown filter accepting several values at once. The trigger shows the label with the selection
 * count; the options (plain text or any node such as a badge) are listed with checkboxes in a portal.
 * When `selectAllLabel` is set, a leading "select all" row toggles every option at once; it is never
 * part of `value`. An option may set `depth` (indentation level) and `searchText` (text matched by
 * the search bar instead of its label, required for a node label).
 */
const MultiSelectFilter = ({
    label,
    options = [],
    value = [],
    showSearchBar = false,
    showSelectedCount = true,
    searchBarPlaceholder,
    selectAllLabel,
    onChange = () => {},
}) => {
    const [open, setOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchBarRef = useRef(null);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        const handleOutsideClick = (e) => {
            const inTrigger = triggerRef.current?.contains(e.target);
            const inDropdown = dropdownRef.current?.contains(e.target);
            if (!inTrigger && !inDropdown) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleOutsideClick);
            if (searchBarRef) {
                searchBarRef.current?.focus();
            }
        } else {
            setSearchText('');
        }
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [open]);

    const handleOpen = () => {
        if (!open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setDropdownPos({
                top: rect.bottom + (window.scrollY ?? 0) + 4,
                left: rect.left + (window.scrollX ?? 0),
            });
        }
        setOpen((o) => !o);
    };

    const toggle = (optionValue) => {
        const next = value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue];
        onChange(next);
    };

    const allSelected = options.length > 0 && options.every((opt) => value.includes(opt.value));

    const toggleAll = () => onChange(allSelected ? [] : options.map((opt) => opt.value));

    const triggerLabel = value.length > 0 ? [label, showSelectedCount ? `(${value.length})` : ''].filter(Boolean).join(' ') : label;

    const filteredOptions = useMemo(() => {
        if (searchText) {
            return options.filter((opt) => (opt.searchText ?? opt.label).toLocaleUpperCase().includes(searchText.toLocaleUpperCase()));
        }
        return options;
    }, [options, searchText]);

    return (
        <div className={style.container}>
            <button
                ref={triggerRef}
                type="button"
                className={`${style.trigger} ${open ? style.triggerOpen : ''} ${value.length > 0 ? style.triggerActive : ''}`}
                onClick={handleOpen}
            >
                <span className={style.triggerLabel}>{triggerLabel}</span>
                <FontAwesomeIcon icon={faChevronDown} className={`${style.chevron} ${open ? style.chevronOpen : ''}`} />
            </button>

            {open &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={`${style.dropdown} ${showSearchBar ? style.dropdownWithSearchBar : ''}`}
                        style={{ top: dropdownPos.top, left: dropdownPos.left }}
                    >
                        {showSearchBar && (
                            <div className={style.dropdownSearchInput}>
                                <InputText
                                    ref={searchBarRef}
                                    value={searchText}
                                    onChange={(evt) => {
                                        setSearchText(evt.target.value);
                                    }}
                                    placeholder={searchBarPlaceholder}
                                />
                            </div>
                        )}
                        {selectAllLabel && (
                            <button type="button" className={style.option} onClick={toggleAll}>
                                <FontAwesomeIcon
                                    icon={allSelected ? faSquareCheck : faSquare}
                                    className={`${style.checkbox} ${allSelected ? style.checkboxChecked : ''}`}
                                />
                                {selectAllLabel}
                            </button>
                        )}
                        {filteredOptions.map((opt) => {
                            const checked = value.includes(opt.value);
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`${style.option} ${opt.depth ? style.indented : ''}`}
                                    style={opt.depth ? { '--depth': opt.depth } : undefined}
                                    aria-pressed={checked}
                                    onClick={() => toggle(opt.value)}
                                >
                                    <FontAwesomeIcon
                                        icon={checked ? faSquareCheck : faSquare}
                                        className={`${style.checkbox} ${checked ? style.checkboxChecked : ''}`}
                                    />
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default MultiSelectFilter;
